import api from './api';

export interface CreatePaymentPreferenceDTO {
  planId: number;
  userId: number;
  couponCode?: string;
  payerName?: string;
  payerEmail?: string;
  payerPhone?: string;
  payerIdentificationType?: string;
  payerIdentificationNumber?: string;
  externalReference?: string;
  description?: string;
  billingInfo?: {
    businessName?: string;
    taxId?: string;
    address?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  };
}

export interface PaymentPreferenceResponseDTO {
  preferenceId: string;
  initPoint: string;
  sandboxInitPoint: string;
  qrCode?: string;
  externalReference: string;
  totalAmount: number;
  currency: string;
  description: string;
  planId: number;
  planName: string;
  originalPrice: number;
  discountApplied: number;
  createdAt: string;
  expiresAt: string;
  status: string;
  isActive: boolean;
  successUrl: string;
  failureUrl: string;
  pendingUrl: string;
  environment: string;
  userSubscriptionId: number;
}

export interface MercadoPagoConfigDTO {
  publicKey: string;
  environment: 'sandbox' | 'production';
  isConfigured: boolean;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  preference?: T;
  message?: string;
  error?: string;
}

class MercadoPagoService {
  private baseUrl = '/api/payments/mercadopago';

  /**
   * Obtiene la configuración pública de MercadoPago
   */
  async getConfig(): Promise<MercadoPagoConfigDTO> {
    try {
      const response = await api.get<MercadoPagoConfigDTO>(`${this.baseUrl}/config`);
      return response.data;
    } catch (error: any) {
      console.error('Error obteniendo configuración de MercadoPago:', error);
      throw new Error(error?.response?.data?.message || 'Error obteniendo configuración de MercadoPago');
    }
  }

  /**
   * Crea una preferencia de pago para Checkout Pro
   */
  async createPaymentPreference(request: CreatePaymentPreferenceDTO): Promise<PaymentPreferenceResponseDTO> {
    try {
      const response = await api.post<ApiResponse<PaymentPreferenceResponseDTO>>(
        `${this.baseUrl}/create-preference`,
        request
      );

      if (!response.data.success) {
        throw new Error(response.data.message || 'Error creando preferencia de pago');
      }

      // El backend puede devolver la preferencia en data o preference
      const preference = response.data.preference || response.data.data;
      if (!preference) {
        throw new Error('Respuesta inválida del servidor');
      }

      return preference;
    } catch (error: any) {
      console.error('Error creando preferencia de pago:', error);
      
      if (error?.response?.data?.error) {
        throw new Error(error.response.data.message || error.response.data.error);
      }
      
      throw new Error(error?.message || 'Error procesando el pago');
    }
  }

  /**
   * Redirige al usuario al checkout de MercadoPago
   */
  redirectToCheckout(preference: PaymentPreferenceResponseDTO): void {
    // Determinar qué URL usar según el entorno
    const checkoutUrl = preference.environment === 'sandbox' 
      ? preference.sandboxInitPoint 
      : preference.initPoint;

    if (!checkoutUrl) {
      throw new Error('URL de checkout no disponible');
    }

    // Guardar información en sessionStorage para cuando regrese
    this.savePaymentSession(preference);

    // Redirigir a MercadoPago
    window.location.href = checkoutUrl;
  }

  /**
   * Guarda información de la sesión de pago
   */
  private savePaymentSession(preference: PaymentPreferenceResponseDTO): void {
    const paymentSession = {
      preferenceId: preference.preferenceId,
      planId: preference.planId,
      planName: preference.planName,
      amount: preference.totalAmount,
      userSubscriptionId: preference.userSubscriptionId,
      timestamp: new Date().toISOString()
    };

    sessionStorage.setItem('mercadopago_session', JSON.stringify(paymentSession));
  }

  /**
   * Obtiene información de la sesión de pago guardada
   */
  getPaymentSession(): any | null {
    const sessionData = sessionStorage.getItem('mercadopago_session');
    if (!sessionData) return null;

    try {
      return JSON.parse(sessionData);
    } catch {
      return null;
    }
  }

  /**
   * Limpia la información de la sesión de pago
   */
  clearPaymentSession(): void {
    sessionStorage.removeItem('mercadopago_session');
  }

  /**
   * Procesa el resultado de un pago cuando el usuario regresa
   */
  async processPaymentResult(params: URLSearchParams): Promise<{
    success: boolean;
    status: string;
    message: string;
    paymentId?: string;
  }> {
    const collection_id = params.get('collection_id');
    const collection_status = params.get('collection_status');
    const external_reference = params.get('external_reference');

    // Determinar el estado basado en los parámetros
    let status = 'unknown';
    let success = false;
    let message = 'Estado de pago desconocido';

    if (collection_status) {
      switch (collection_status) {
        case 'approved':
          status = 'approved';
          success = true;
          message = '¡Pago aprobado! Tu suscripción se activará en unos momentos.';
          break;
        case 'pending':
          status = 'pending';
          success = true; // No es error, pero no está completado
          message = 'Tu pago está siendo procesado. Te notificaremos cuando se complete.';
          break;
        case 'rejected':
        case 'failure':
          status = 'failed';
          success = false;
          message = 'El pago fue rechazado. Por favor, intenta con otro método de pago.';
          break;
        default:
          status = collection_status;
      }
    }

    return {
      success,
      status,
      message,
      paymentId: collection_id || undefined
    };
  }

  /**
   * Crea un pago completo (preferencia + redirección)
   */
  async createPaymentAndRedirect(request: CreatePaymentPreferenceDTO): Promise<void> {
    try {
      // 1. Crear la preferencia
      const preference = await this.createPaymentPreference(request);
      
      // 2. Redirigir al checkout
      this.redirectToCheckout(preference);
    } catch (error) {
      console.error('Error en flujo de pago:', error);
      throw error;
    }
  }

  /**
   * Valida si MercadoPago está correctamente configurado
   */
  async validateConfiguration(): Promise<boolean> {
    try {
      const config = await this.getConfig();
      return config.isConfigured && !!config.publicKey;
    } catch {
      return false;
    }
  }
  /**
   * Pago directo (Checkout Transparente)
   */
  async directPayment(payload: {
    userId: number;
    planId: number;
    token: string;
    payerEmail: string;
    payerFirstName?: string;
    payerLastName?: string;
    installments?: number;
    paymentMethodId?: string;
    issuerId?: string;
    description?: string;
    externalReference?: string;
    couponCode?: string;
    metadata?: any;
    transactionAmount: number;
    savePaymentMethod?: boolean;
  }): Promise<{ success: boolean; payment: { paymentId: string; status: string; statusDetail: string; subscriptionId: number } }> {
    const res = await api.post(`${this.baseUrl}/direct-payment`, payload);
    return res.data;
  }
}

// Exportar instancia singleton
export const mercadoPagoService = new MercadoPagoService();
export default mercadoPagoService;